import React from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { fieldMessage, hasError } from './blockUtils';

export const GENERAL_POOL_ID = 'sa-general-pool';
const ERROR_ID = `${GENERAL_POOL_ID}-error`;
const HOURS_ID = 'sa-availability-hours';

const DAYS_OF_WEEK = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
];

export const EMPTY_AVAILABILITY = { hoursPerWeek: '', schedule: [] };

// answers.generalPool is true, false, or unset. There is no default.
export function getErrors(answers) {
    if (typeof answers?.generalPool === 'boolean') return [];
    return [{ id: GENERAL_POOL_ID, label: 'General pool question' }];
}

export function isComplete(answers) {
    return getErrors(answers).length === 0;
}

function GeneralPool({ answers, onChange, errorIds, context }) {
    const roleName = context?.roleName;
    const availability = answers.availability || EMPTY_AVAILABILITY;
    const serverMessage = fieldMessage(context, GENERAL_POOL_ID);
    const invalid =
        hasError(errorIds, GENERAL_POOL_ID) || Boolean(serverMessage);

    const setAvailability = (next) => onChange('availability', next);

    const updateSlot = (index, field, value) =>
        setAvailability({
            ...availability,
            schedule: availability.schedule.map((slot, i) =>
                i === index ? { ...slot, [field]: value } : slot
            ),
        });

    return (
        <div>
            <fieldset
                role="radiogroup"
                className="mb-3"
                aria-required="true"
                aria-invalid={invalid}
                aria-describedby={invalid ? ERROR_ID : undefined}
            >
                <legend className="fs-6 fw-semibold mb-2">
                    General pool: would you like to be considered for any
                    available {roleName} appointment, even if it isn&apos;t
                    listed above?{' '}
                    <span className="text-danger" aria-hidden="true">
                        *
                    </span>
                </legend>
                <Form.Check
                    type="radio"
                    id={GENERAL_POOL_ID}
                    name="sa-general-pool"
                    label="Yes - consider me for any available appointment"
                    checked={answers.generalPool === true}
                    onChange={() => onChange('generalPool', true)}
                />
                <Form.Check
                    type="radio"
                    id={`${GENERAL_POOL_ID}-no`}
                    name="sa-general-pool"
                    label="No - only the course(s) I selected"
                    checked={answers.generalPool === false}
                    onChange={() => onChange('generalPool', false)}
                />
                {invalid && (
                    <Form.Control.Feedback
                        type="invalid"
                        id={ERROR_ID}
                        role="alert"
                        className="d-block"
                    >
                        {serverMessage || 'Answer the general pool question.'}
                    </Form.Control.Feedback>
                )}
            </fieldset>

            <div aria-live="polite">
                {answers.generalPool === true &&
                    !context?.availabilityElsewhere && (
                        <div>
                            <h2 className="h5 mb-2">Weekly availability</h2>
                            <p className="text-muted small">
                                This helps the program match you to a section
                                that fits your schedule.
                            </p>
                            <Row>
                                <Col md={4}>
                                    <Form.Group
                                        className="mb-3"
                                        controlId={HOURS_ID}
                                    >
                                        <Form.Label>
                                            Hours Available Per Week
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            min="1"
                                            max="40"
                                            placeholder="e.g., 10"
                                            value={availability.hoursPerWeek}
                                            onChange={(e) =>
                                                setAvailability({
                                                    ...availability,
                                                    hoursPerWeek:
                                                        e.target.value,
                                                })
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <h3 className="h6 mb-1">
                                        Schedule Availability
                                    </h3>
                                    <p className="text-muted small mb-0">
                                        Add time slots when you are available.
                                    </p>
                                </div>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() =>
                                        setAvailability({
                                            ...availability,
                                            schedule: [
                                                ...availability.schedule,
                                                {
                                                    day: 'Monday',
                                                    startTime: '09:00',
                                                    endTime: '10:00',
                                                },
                                            ],
                                        })
                                    }
                                >
                                    + Add Time Slot
                                </Button>
                            </div>

                            {availability.schedule.map((slot, index) => (
                                <Card
                                    key={index}
                                    className="mb-2 border-0 shadow-sm"
                                >
                                    <Card.Body className="py-2">
                                        <Row className="align-items-center g-2">
                                            <Col sm={4}>
                                                <Form.Select
                                                    size="sm"
                                                    aria-label={`Day for time slot ${index + 1}`}
                                                    value={slot.day}
                                                    onChange={(e) =>
                                                        updateSlot(
                                                            index,
                                                            'day',
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    {DAYS_OF_WEEK.map((day) => (
                                                        <option
                                                            key={day}
                                                            value={day}
                                                        >
                                                            {day}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Col>
                                            <Col xs={5} sm={3}>
                                                <Form.Control
                                                    type="time"
                                                    size="sm"
                                                    aria-label={`Start time for time slot ${index + 1}`}
                                                    value={slot.startTime}
                                                    onChange={(e) =>
                                                        updateSlot(
                                                            index,
                                                            'startTime',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </Col>
                                            <Col xs={5} sm={3}>
                                                <Form.Control
                                                    type="time"
                                                    size="sm"
                                                    aria-label={`End time for time slot ${index + 1}`}
                                                    value={slot.endTime}
                                                    onChange={(e) =>
                                                        updateSlot(
                                                            index,
                                                            'endTime',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </Col>
                                            <Col xs={2} className="text-end">
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    aria-label={`Remove time slot ${index + 1}`}
                                                    onClick={() =>
                                                        setAvailability({
                                                            ...availability,
                                                            schedule:
                                                                availability.schedule.filter(
                                                                    (_s, i) =>
                                                                        i !==
                                                                        index
                                                                ),
                                                        })
                                                    }
                                                >
                                                    &times;
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}
            </div>
        </div>
    );
}

export default GeneralPool;
